import { DateTime } from "luxon";
import { Table } from "dexie";

import { BaseDexieRepository } from "../base-repository";
import type { LocalAudioRecordingTranscriptTurnRecord } from "@/domains/dexie-db/models/audio-recording";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";

export type UpsertLocalAudioRecordingTranscriptTurnData = Omit<
  LocalAudioRecordingTranscriptTurnRecord,
  "id" | "transcriptTurnIndex" | "createdAt" | "updatedAt" | "associatedAudioRecordingId"
>;

export class LocalAudioRecordingTranscriptTurnsRepository extends BaseDexieRepository<LocalAudioRecordingTranscriptTurnRecord> {
  constructor({
    tableRef,
  }: {
    tableRef: () => Table<LocalAudioRecordingTranscriptTurnRecord, number>;
  }) {
    super({ tableRef });
  }

  async upsert({
    associatedAudioRecordingId,
    transcriptTurnIndex,
    data,
  }: {
    associatedAudioRecordingId: Uuid;
    transcriptTurnIndex: number;
    data: UpsertLocalAudioRecordingTranscriptTurnData;
  }): Promise<void> {
    await this.transaction("rw", async () => {
      await this.table.where({ associatedAudioRecordingId, transcriptTurnIndex }).delete();

      const id = uuidModule.resolveLocalAudioRecordingTranscriptTurnId({
        associatedAudioRecordingId,
        transcriptTurnIndex,
      });

      const now = DateTime.now();

      await this.table.add({
        id,
        ...data,
        associatedAudioRecordingId,
        transcriptTurnIndex,
        createdAt: now,
        updatedAt: now,
      });
    });
  }

  async update({
    associatedAudioRecordingId,
    transcriptTurnIndex,
    data,
  }: {
    associatedAudioRecordingId: Uuid;
    transcriptTurnIndex: number;
    data: Partial<UpsertLocalAudioRecordingTranscriptTurnData>;
  }): Promise<void> {
    await this.table
      .where({ associatedAudioRecordingId, transcriptTurnIndex })
      .modify({ ...data, updatedAt: DateTime.now() });
  }

  async getAllByRecordingId({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): Promise<LocalAudioRecordingTranscriptTurnRecord[]> {
    return this.table.where({ associatedAudioRecordingId }).sortBy("transcriptTurnIndex");
  }

  async deleteAllByRecordingId({
    associatedAudioRecordingId,
  }: {
    associatedAudioRecordingId: Uuid;
  }): Promise<void> {
    await this.table.where({ associatedAudioRecordingId }).delete();
  }
}
